import React, { useCallback, useEffect, useMemo, useState } from "react";
import ActionButton from "../../components/ui/ActionButton";
import AdvancedFilters from "../../components/ui/AdvancedFilters";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { PageHeading } from "../../components/ui/PageHeading";
import QRCodePreview from "../../components/ui/QRCodePreview";
import SearchInput from "../../components/ui/SearchInput";
import { useToast } from "../../components/ui/toast/ToastProvider";
import {
  assertSuccessfulResponse,
  getRequestErrorMessage,
  getResponseMessage,
} from "../../services/apiMessages";
import MasterForm from "./MasterForm";
import MasterTable from "./MasterTable";
import useResolvedFilterFields from "./useResolvedFilterFields";
import { usePlantSelection } from "../../features/plants/PlantSelectionContext";

const getRecordUid = (row, idField) =>
  row?.uid || row?._id || row?.id || row?.[idField];

const getSearchValue = (row, fieldName, metaData) => {
  const value =
    typeof fieldName === "function" ? fieldName(row, metaData) : row?.[fieldName];
  if (typeof value === "boolean") return value ? "yes true active" : "no false inactive";
  return value ?? "";
};

const MasterCrudPage = ({
  entityName,
  pageTitle,
  searchPlaceholder,
  idField,
  displayField,
  fields,
  columns,
  searchFields,
  useListQuery,
  useCreateMutation,
  useUpdateMutation,
  useDeleteMutation,
  transformPayload,
  noDataLabel,
  onOpenForm,
  onAfterSave,
  extraFormContent,
  formDisabled = false,
  qrDisplayValue,
  qrValue,
  rowActions,
  showQrColumn = true,
  showPlantFilter = true,
  filterFields = [],
}) => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const { selectedPlantId: globalPlantId } = usePlantSelection();
  const selectedPlantId = showPlantFilter ? globalPlantId : "";
  const [filterValues, setFilterValues] = useState({});
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [pendingUpdatePayload, setPendingUpdatePayload] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isSavingRelated, setIsSavingRelated] = useState(false);
  const [saveError, setSaveError] = useState("");
  const toast = useToast();
  const resolvedFilterFields = useResolvedFilterFields(
    filterFields,
    showPlantFilter ? selectedPlantId : ""
  );

  useEffect(() => {
    setIsFormOpen(false);
    setEditData(null);
    setPendingUpdatePayload(null);
    setFilterValues({});
    setPage(1);
  }, [selectedPlantId]);

  const listQueryArgs = useMemo(
    () => ({
      page,
      limit: rowsPerPage,
      ...filterValues,
      ...(showPlantFilter && selectedPlantId
        ? { plant_id: selectedPlantId }
        : {}),
    }),
    [filterValues, page, rowsPerPage, selectedPlantId, showPlantFilter]
  );

  const { data, isFetching } = useListQuery(listQueryArgs);
  const [createRecord, { isLoading: isCreating }] = useCreateMutation();
  const [updateRecord, { isLoading: isUpdating }] = useUpdateMutation();
  const [deleteRecord, { isLoading: isDeleting }] = useDeleteMutation();

  const tableRows = useMemo(() => data?.data || [], [data?.data]);
  const responseMetaData = useMemo(() => data?.meta_data || {}, [data?.meta_data]);
  const totalRecords = data?.meta_data?.total_records ?? tableRows.length;
  const resolvedColumns = useMemo(
    () => (typeof columns === "function" ? columns(responseMetaData) : columns),
    [columns, responseMetaData]
  );

  const rows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return tableRows;

    return tableRows.filter((row) =>
      searchFields.some((fieldName) =>
        String(getSearchValue(row, fieldName, responseMetaData))
          .toLowerCase()
          .includes(normalizedSearch)
      )
    );
  }, [responseMetaData, searchFields, searchTerm, tableRows]);

  const handleEditClick = useCallback((row) => {
    setSaveError("");
    onOpenForm?.(row);
    setEditData(row);
    setPendingUpdatePayload(null);
    setIsFormOpen(true);
  }, [onOpenForm]);

  const handleDeleteClick = useCallback((row) => {
    setDeleteTarget(row);
  }, []);

  const handleCreate = async (payload) => {
    if (formDisabled || isSavingRelated) return;
    setSaveError("");
    let savedRecord;
    try {
      const createPayload = showPlantFilter
        ? { ...payload, plant_id: selectedPlantId }
        : payload;
      const response = assertSuccessfulResponse(
        await createRecord(createPayload).unwrap(),
        `Unable to create ${entityName.toLowerCase()}.`
      );
      if (onAfterSave) {
        const responseRecord = Array.isArray(response?.data) ? response.data[0] : response?.data;
        savedRecord = { ...createPayload, ...responseRecord };
        setIsSavingRelated(true);
        await onAfterSave(savedRecord);
      }
      toast.success(
        getResponseMessage(response, `${entityName} created successfully.`)
      );
      setIsFormOpen(false);
      setSearchTerm("");
    } catch (error) {
      if (savedRecord) {
        setEditData(savedRecord);
        setSaveError(`${entityName} was created, but its operation mappings could not be saved. Retry with Update ${entityName}. ${getRequestErrorMessage(error)}`);
        return;
      }
      toast.error(
        getRequestErrorMessage(error, `Unable to create ${entityName.toLowerCase()}.`)
      );
    } finally {
      setIsSavingRelated(false);
    }
  };

  const handleConfirmUpdate = async () => {
    const uid = getRecordUid(editData, idField);
    if (!uid || !pendingUpdatePayload || formDisabled || isSavingRelated) return;
    setSaveError("");
    let recordSaved = false;

    try {
      const response = assertSuccessfulResponse(
        await updateRecord({ uid, ...pendingUpdatePayload }).unwrap(),
        `Unable to update ${entityName.toLowerCase()}.`
      );
      recordSaved = true;
      if (onAfterSave) {
        setIsSavingRelated(true);
        await onAfterSave({ ...editData, ...pendingUpdatePayload });
      }
      toast.success(
        getResponseMessage(response, `${entityName} updated successfully.`)
      );
      setPendingUpdatePayload(null);
      setEditData(null);
      setIsFormOpen(false);
    } catch (error) {
      if (recordSaved) {
        setPendingUpdatePayload(null);
        setSaveError(`${entityName} details were saved, but its operation mappings could not be fully saved. Retry Update ${entityName}. ${getRequestErrorMessage(error)}`);
        return;
      }
      toast.error(
        getRequestErrorMessage(error, `Unable to update ${entityName.toLowerCase()}.`)
      );
    } finally {
      setIsSavingRelated(false);
    }
  };

  const handleConfirmDelete = async () => {
    const uid = getRecordUid(deleteTarget, idField);
    if (!uid) return;

    try {
      const response = assertSuccessfulResponse(
        await deleteRecord(uid).unwrap(),
        `Unable to delete ${entityName.toLowerCase()}.`
      );
      toast.success(
        getResponseMessage(response, `${entityName} deleted successfully.`)
      );
      setDeleteTarget(null);
    } catch (error) {
      toast.error(
        getRequestErrorMessage(error, `Unable to delete ${entityName.toLowerCase()}.`)
      );
      setDeleteTarget(null);
    }
  };

  const handleRowsPerPageChange = (newLimit) => {
    setRowsPerPage(newLimit);
    setPage(1);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleFilterChange = (name, value) => {
    setFilterValues((current) => ({
      ...current,
      [name]: value,
    }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilterValues({});
    setPage(1);
  };

  const deleteLabel = deleteTarget?.[displayField] || deleteTarget?.[idField] || entityName;
  const hasAdvancedFilters = Object.values(filterValues).some(
    (value) => value !== undefined && value !== null && String(value).trim() !== ""
  );
  const hasActiveFilters = Boolean(searchTerm || selectedPlantId || hasAdvancedFilters);
  const searchedRecords = searchTerm ? rows.length : totalRecords;
  const editQrValue = qrValue && editData ? qrValue(editData) : "";
  const deleteQrValue = qrValue && deleteTarget ? qrValue(deleteTarget) : "";
  const editQrDisplayValue =
    qrDisplayValue && editData ? qrDisplayValue(editData) : "";
  const deleteQrDisplayValue =
    qrDisplayValue && deleteTarget ? qrDisplayValue(deleteTarget) : "";

  return (
    <div className="px-3 sm:px-5">
      <PageHeading title={pageTitle} />

      <div className="flex flex-col gap-3 pb-3 lg:flex-row lg:items-center lg:justify-between">
        <SearchInput
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder={searchPlaceholder || `Search ${pageTitle.toLowerCase()}`}
        />
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          <AdvancedFilters
            fields={resolvedFilterFields}
            values={filterValues}
            onChange={handleFilterChange}
            onClear={handleClearFilters}
            requirePlant={showPlantFilter}
            hasPlant={Boolean(selectedPlantId)}
          />
          <ActionButton
            action="add"
            label={`Add ${entityName}`}
            onClick={() => {
              setSaveError("");
              onOpenForm?.(null);
              setEditData(null);
              setPendingUpdatePayload(null);
              setIsFormOpen(true);
            }}
            disabled={showPlantFilter && !selectedPlantId}
            title={showPlantFilter && !selectedPlantId ? "Select a plant from the top bar first" : undefined}
            className="w-full sm:w-auto"
          />
        </div>
      </div>

      <MasterTable
        columns={resolvedColumns}
        data={rows}
        entityName={entityName}
        isFetching={isFetching}
        noDataComponent={
          hasActiveFilters
            ? `No matching ${pageTitle.toLowerCase()} found`
            : noDataLabel
        }
        onDeleteClick={handleDeleteClick}
        onEditClick={handleEditClick}
        onPageChange={setPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        page={page}
        qrDisplayValue={qrDisplayValue}
        qrValue={showQrColumn ? qrValue : undefined}
        rowActions={rowActions}
        rowsPerPage={rowsPerPage}
        searchedRecords={searchedRecords}
        totalRecords={totalRecords}
      />

      <MasterForm
        plantId={showPlantFilter ? selectedPlantId : undefined}
        entityName={entityName}
        editData={editData}
        fields={showPlantFilter ? fields.filter((field) => field.name !== "plant_id") : fields}
        isOpen={isFormOpen}
        isSaving={isCreating || isUpdating || isSavingRelated}
        submitDisabled={formDisabled}
        extraContent={extraFormContent}
        saveError={saveError}
        onClose={() => {
          setIsFormOpen(false);
          setPendingUpdatePayload(null);
        }}
        onCreate={handleCreate}
        onRequestUpdate={setPendingUpdatePayload}
        qrDisplayValue={editQrDisplayValue}
        qrValue={editQrValue}
        transformPayload={transformPayload}
      />

      <ConfirmDialog
        open={Boolean(pendingUpdatePayload)}
        onOpenChange={(open) => {
          if (!open && !isUpdating && !isSavingRelated) setPendingUpdatePayload(null);
        }}
        title={`Update ${entityName.toLowerCase()}?`}
        description={`The ${entityName.toLowerCase()} details will be updated with the current values.`}
        confirmLabel="Update"
        onConfirm={handleConfirmUpdate}
        isLoading={isUpdating || isSavingRelated}
      >
        {editQrValue && (
          <div className="rounded-lg bg-slate-50 px-4 py-3">
            <QRCodePreview
              value={editQrValue}
              displayValue={editQrDisplayValue}
              label={`${entityName} ${editQrValue}`}
              size={104}
              showValue
              className="mx-auto"
            />
          </div>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={`Delete ${entityName.toLowerCase()}?`}
        description={`${deleteLabel} will be permanently deleted.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      >
        {deleteQrValue && (
          <div className="rounded-lg bg-red-50 px-4 py-3">
            <QRCodePreview
              value={deleteQrValue}
              displayValue={deleteQrDisplayValue}
              label={`${entityName} ${deleteQrValue}`}
              size={104}
              showValue
              className="mx-auto"
            />
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
};

export default MasterCrudPage;
